/**
 * WMS 3PL Multi-Client Types
 * Shared between backend-wms, web-wms, and mobile-app
 *
 * DRY Compliance: Single source for all client-related types and enums
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Client status in the system
 */
export enum ClientStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
  ONBOARDING = 'ONBOARDING',
}

/**
 * Billing type determines how client is charged
 */
export enum BillingType {
  STORAGE = 'STORAGE',
  TRANSACTION = 'TRANSACTION',
  HYBRID = 'HYBRID',
  CUSTOM = 'CUSTOM',
}

/**
 * Storage unit for billing calculations
 */
export enum StorageUnit {
  PALLET = 'PALLET',
  CASE = 'CASE',
  EACH = 'EACH',
  CUBIC_METER = 'CUBIC_METER',
  SQUARE_METER = 'SQUARE_METER',
}

/**
 * Allocation strategy for client storage
 */
export enum AllocationStrategy {
  SHARED = 'SHARED',
  ZONE_DEDICATED = 'ZONE_DEDICATED',
  LOCATION_DEDICATED = 'LOCATION_DEDICATED',
  MIXED = 'MIXED',
}

/**
 * Client access level for users
 */
export enum ClientAccessLevel {
  NONE = 'NONE',
  SINGLE = 'SINGLE',
  MULTIPLE = 'MULTIPLE',
  ALL = 'ALL',
}

/**
 * Billing cycle options
 */
export enum BillingCycle {
  WEEKLY = 'WEEKLY',
  BIWEEKLY = 'BIWEEKLY',
  MONTHLY = 'MONTHLY',
}

/**
 * Putaway/Pick strategy options
 */
export enum InventoryStrategy {
  FIFO = 'FIFO',
  LIFO = 'LIFO',
  FEFO = 'FEFO',
  DIRECTED = 'DIRECTED',
}

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Client billing settings
 */
export interface ClientBillingSettings {
  billing_type: BillingType;
  currency: string; // ISO 4217 - Default: 'USD' (USA-first strategy)

  // Storage rates
  storage_unit?: StorageUnit;
  storage_rate_per_unit_day?: number;
  minimum_storage_fee_monthly?: number;

  // Transaction rates
  receiving_rate_per_line?: number;
  shipping_rate_per_line?: number;
  picking_rate_per_line?: number;

  // Value-added services
  labeling_rate_per_unit?: number;
  kitting_rate_per_unit?: number;
  returns_processing_rate?: number;

  // Terms
  billing_cycle: BillingCycle;
  payment_terms_days: number; // Net 30, Net 60, etc.

  // Contact for billing
  billing_email?: string;
  billing_contact_name?: string;
}

/**
 * Client storage preferences
 */
export interface ClientStoragePreferences {
  allocation_strategy: AllocationStrategy;

  // Zone preferences
  preferred_zone_types?: string[];
  avoid_zone_types?: string[];

  // Location preferences
  preferred_location_types?: string[];
  max_stack_height?: number;

  // Putaway rules
  putaway_strategy: InventoryStrategy;
  consolidate_products: boolean;

  /**
   * Maximum gap (in days) between the new lot's expiry and the earliest
   * expiry already at a candidate location before the placement is
   * rejected as a FEFO violation.
   *
   * `0` (default) = strict: do NOT mix a new lot into a location whose
   * existing inventory expires BEFORE the new lot — the new lot would fall
   * behind older stock and miss its pick window.
   *
   * `N > 0` = allow mixing when the gap is ≤ N days; this is useful for
   * fast-moving inventory where occasional lot mixing is acceptable.
   *
   * Only consulted when `putaway_strategy = FEFO` and the product is
   * `is_expiry_controlled = true`.
   */
  fefo_mix_tolerance_days?: number;

  // Picking preferences
  pick_strategy: InventoryStrategy;
  allow_partial_pick: boolean;
}

/**
 * Client context for request
 */
export interface ClientContext {
  tenant_id: string;
  client_id?: string;
  client_ids?: string[];
  is_admin: boolean;
}

/**
 * Client inventory summary for reporting
 */
export interface ClientInventorySummary {
  client_id: string;
  client_code: string;
  client_name: string;

  // Counts
  total_sku_count: number;
  total_locations_used: number;
  total_units_on_hand: number;
  total_units_reserved: number;
  total_units_available: number;

  // Value
  total_inventory_value: number;
  currency: string;

  // Storage
  pallet_positions_used: number;
  storage_utilization_percentage: number;

  // Activity
  received_units_30d: number;
  shipped_units_30d: number;

  // Health
  items_expiring_30d: number;
  items_low_stock: number;
  items_in_quarantine: number;
}

// ============================================================================
// UI LABELS & COLORS
// ============================================================================

/**
 * Status labels for UI display
 */
export const ClientStatusLabels: Record<ClientStatus, string> = {
  [ClientStatus.ACTIVE]: 'Active',
  [ClientStatus.INACTIVE]: 'Inactive',
  [ClientStatus.SUSPENDED]: 'Suspended',
  [ClientStatus.ONBOARDING]: 'Onboarding',
};

/**
 * Color schemes for status badges (Chakra UI v3 compatible)
 */
export const ClientStatusColors: Record<ClientStatus, string> = {
  [ClientStatus.ACTIVE]: 'green',
  [ClientStatus.INACTIVE]: 'gray',
  [ClientStatus.SUSPENDED]: 'red',
  [ClientStatus.ONBOARDING]: 'blue',
};

export const BillingTypeLabels: Record<BillingType, string> = {
  [BillingType.STORAGE]: 'Storage',
  [BillingType.TRANSACTION]: 'Transaction',
  [BillingType.HYBRID]: 'Hybrid',
  [BillingType.CUSTOM]: 'Custom',
};

export const AllocationStrategyLabels: Record<AllocationStrategy, string> = {
  [AllocationStrategy.SHARED]: 'Shared',
  [AllocationStrategy.ZONE_DEDICATED]: 'Zone Dedicated',
  [AllocationStrategy.LOCATION_DEDICATED]: 'Location Dedicated',
  [AllocationStrategy.MIXED]: 'Mixed',
};

// ============================================================================
// CONSTANTS
// ============================================================================

export const CLIENT_STATUS_VALUES = ['ACTIVE', 'INACTIVE', 'SUSPENDED', 'ONBOARDING'] as const;
export const BILLING_TYPE_VALUES = ['STORAGE', 'TRANSACTION', 'HYBRID', 'CUSTOM'] as const;
export const STORAGE_UNIT_VALUES = [
  'PALLET',
  'CASE',
  'EACH',
  'CUBIC_METER',
  'SQUARE_METER',
] as const;
export const ALLOCATION_STRATEGY_VALUES = [
  'SHARED',
  'ZONE_DEDICATED',
  'LOCATION_DEDICATED',
  'MIXED',
] as const;
export const CLIENT_ACCESS_LEVEL_VALUES = ['NONE', 'SINGLE', 'MULTIPLE', 'ALL'] as const;
export const BILLING_CYCLE_VALUES = ['WEEKLY', 'BIWEEKLY', 'MONTHLY'] as const;
export const INVENTORY_STRATEGY_VALUES = ['FIFO', 'LIFO', 'FEFO', 'DIRECTED'] as const;

/**
 * Default billing settings for new clients (USD - USA-first)
 */
export const DEFAULT_BILLING_SETTINGS: ClientBillingSettings = {
  billing_type: BillingType.STORAGE,
  currency: 'USD',
  billing_cycle: BillingCycle.MONTHLY,
  payment_terms_days: 30,
};

/**
 * Default storage preferences for new clients
 */
export const DEFAULT_STORAGE_PREFERENCES: ClientStoragePreferences = {
  allocation_strategy: AllocationStrategy.SHARED,
  putaway_strategy: InventoryStrategy.FIFO,
  consolidate_products: true,
  fefo_mix_tolerance_days: 0,
  pick_strategy: InventoryStrategy.FIFO,
  allow_partial_pick: true,
};
